import { useCallback, useEffect, useRef, useState } from 'react';
import type { LeadData } from '@hub/shared-types';
import { usePinAuthentication } from '@/presentation/hooks/usePinAuthentication';
import { useFakeLeadCapture } from '@/presentation/hooks/useFakeLeadCapture';
import { useEventConfig } from '@/presentation/hooks/useEventConfig';
import { useLeadQueueSync } from '@/presentation/hooks/useLeadQueueSync';
import type { LeadQueueIndicatorState } from '@/presentation/hooks/useLeadQueueSync';
import { usePluginRegistryResolution } from '@/presentation/hooks/usePluginRegistryResolution';
import type { PluginRegistryResolutionOutcome } from '@/presentation/hooks/usePluginRegistryResolution';
import { usePluginLoadRetry } from '@/presentation/hooks/usePluginLoadRetry';
import { usePluginLeadCapture } from '@/presentation/hooks/usePluginLeadCapture';
import { RecordPluginLoadEventUseCase } from '@/application/use-cases/RecordPluginLoadEventUseCase';
import { HttpPluginLoadTelemetryRepository } from '@/infrastructure/repositories/HttpPluginLoadTelemetryRepository';
import type { PluginLoadEvent } from '@/domain/repositories/IPluginLoadTelemetryRepository';
import { IndexedDbActiveSessionRepository } from '@/infrastructure/repositories/IndexedDbActiveSessionRepository';
import {
  isActiveSessionExpired,
  toDeviceSessionFromSnapshot,
} from '@/domain/entities/ActiveSessionSnapshot';
import { readActivationType } from '@/domain/entities/EventConfig';
import { resolveConsentVersion } from '@/domain/entities/EventConsent';
import type { EventConsent } from '@/domain/entities/EventConsent';
import type { DeviceSession } from '@/domain/entities/DeviceSession';
import type { FakeLeadInput } from '@/domain/entities/FakeLead';
import type { PluginRegistryEntry } from '@/domain/entities/PluginRegistryEntry';

/** Identifica o dispositivo perante o `manager-api` (HUB-21). Não é exposto
 * ao operador nesta tela fake/piloto — apenas rotula a sub-sessão. */
const DEVICE_LABEL = 'Ativação Fake — Piloto';

const activeSessionRepository = new IndexedDbActiveSessionRepository();
const recordPluginLoadEventUseCase = new RecordPluginLoadEventUseCase(
  new HttpPluginLoadTelemetryRepository()
);

export type FlowStep =
  | { kind: 'pin-entry' }
  | { kind: 'loading-config'; session: DeviceSession }
  | { kind: 'session-expired'; expiredAt: Date }
  | { kind: 'lead-capture'; session: DeviceSession; consent: EventConsent | null }
  | {
      kind: 'success';
      session: DeviceSession;
      consent: EventConsent | null;
      leadName: string | null;
    }
  // HUB-237 — resolução do registro de plugin (CA-01), logo após a config
  // do evento carregar. Renderizado a partir de `pluginRegistry.state`
  // (attempting/waiting/exhausted) — este passo em si só guarda o contexto
  // necessário para retentar (`activationType`) e para decidir o próximo
  // passo quando a resolução terminar.
  | {
      kind: 'resolving-plugin';
      session: DeviceSession;
      consent: EventConsent | null;
      activationConfig: Record<string, unknown>;
      activationType: string;
    }
  // CA-11: tipo sem entrada no registro — terminal.
  | { kind: 'plugin-not-configured' }
  // CA-18: consentimento dedicado antes de montar o plugin real.
  | {
      kind: 'plugin-consent-gate';
      session: DeviceSession;
      consent: EventConsent | null;
      entry: PluginRegistryEntry;
      activationConfig: Record<string, unknown>;
    }
  // CA-04 a CA-10: carregamento/render do plugin real. Renderizado a partir
  // de `pluginLoad.state` (loading/waiting/ready/error-final).
  | {
      kind: 'plugin';
      session: DeviceSession;
      consent: EventConsent | null;
      entry: PluginRegistryEntry;
      activationConfig: Record<string, unknown>;
      consentAccepted: boolean;
      consentTextVersion: number;
    };

export interface UseActivationFlowResult {
  step: FlowStep;
  pinAuth: ReturnType<typeof usePinAuthentication>;
  eventConfig: ReturnType<typeof useEventConfig>;
  leadCapture: ReturnType<typeof useFakeLeadCapture>;
  pluginRegistry: ReturnType<typeof usePluginRegistryResolution>;
  pluginLoad: ReturnType<typeof usePluginLoadRetry>;
  /** Estado do indicador de sincronização (HUB-236, CA-07) e contagem de
   * leads pendentes na fila — repassados para `OnlineStatusIndicator`. */
  indicatorState: LeadQueueIndicatorState;
  pendingCount: number;
  /** `true` quando a fila atingiu 9.500 leads no último enfileiramento
   * (CA-08) — controla a exibição de `QueueAlertToast`. */
  queueAlertVisible: boolean;
  /** `true` quando a última tentativa de criar um lead foi bloqueada por a
   * fila já ter 10.000 registros (CA-09) — controla `QueueFullModal`. */
  queueFullVisible: boolean;
  dismissQueueAlert: () => void;
  closeQueueFullModal: () => void;
  handlePinSubmit: (pin: string) => Promise<void>;
  handleRetryConfig: () => void;
  handleBackFromExpired: () => void;
  /** Gesto de encerramento explícito (LOCAL-003, ADR-003 Adendo 2026-07-30):
   * limpa a sessão e volta a `pin-entry` a partir de qualquer passo do
   * fluxo, sem passar por `SessionExpiredScreen`. */
  handleEndSession: () => void;
  handleLeadSubmit: (input: FakeLeadInput) => Promise<void>;
  handleCaptureAnother: () => void;
  handleRetryPluginRegistry: () => void;
  handlePluginConsentApprove: () => void;
  handlePluginConsentDecline: () => void;
  handlePluginLeadCapture: (lead: LeadData) => void;
  handlePluginComplete: () => void;
}

/**
 * Orquestra o fluxo de ativação: valida o PIN (`POST /events/auth-pin`), só
 * então carrega a configuração de ativação autenticada
 * (`GET /events/:id/config`, HUB-235 — nunca antes do PIN, CA-01) e resolve
 * o registro de plugin do tipo do evento (`GET /plugin-registry/{type}`,
 * HUB-237, ver ADR-010): `isReady=false` preserva o caminho de ativação
 * fake existente (HUB-147, zero regressão — CA-02); `isReady=true` segue
 * para o consent gate (CA-18) e o carregamento do plugin real (CA-04 a
 * CA-10) via Module Federation.
 *
 * Uma sessão de dispositivo que expira em uso (HTTP 410, CA-03) troca para
 * a tela de encerramento dedicada durante PIN/config/registro de plugin.
 */
export function useActivationFlow(): UseActivationFlowResult {
  const [step, setStep] = useState<FlowStep>({ kind: 'pin-entry' });
  const [queueAlertVisible, setQueueAlertVisible] = useState(false);
  const [queueFullVisible, setQueueFullVisible] = useState(false);
  const pinAuth = usePinAuthentication();
  const eventConfig = useEventConfig();
  const leadCapture = useFakeLeadCapture();
  const pluginRegistry = usePluginRegistryResolution();
  const pluginLoad = usePluginLoadRetry();
  const pluginLeadCapture = usePluginLeadCapture();
  const { indicatorState, pendingCount, notifyLeadEnqueued } = useLeadQueueSync();
  const reportedLoadStateRef = useRef<typeof pluginLoad.state | null>(null);

  const applyPluginRegistryOutcome = useCallback(
    (
      outcome: PluginRegistryResolutionOutcome,
      session: DeviceSession,
      consent: EventConsent | null,
      activationConfig: Record<string, unknown>
    ): void => {
      if (outcome.kind === 'not-configured') {
        setStep({ kind: 'plugin-not-configured' });
        return;
      }
      if (outcome.kind === 'session-expired') {
        setStep({ kind: 'session-expired', expiredAt: outcome.expiredAt });
        return;
      }
      if (outcome.kind === 'exhausted') {
        // Permanece em 'resolving-plugin' — a tela já reage a
        // `pluginRegistry.state` (exhausted) com o botão de retry (CA-16).
        return;
      }
      // CA-02: isReady=false preserva o caminho fake, sem nenhuma mudança.
      if (!outcome.entry.isReady) {
        setStep({ kind: 'lead-capture', session, consent });
        return;
      }
      setStep({
        kind: 'plugin-consent-gate',
        session,
        consent,
        entry: outcome.entry,
        activationConfig,
      });
    },
    []
  );

  const runLoadConfig = useCallback(
    async (session: DeviceSession): Promise<void> => {
      const result = await eventConfig.loadConfig(session);

      if (!result.success && result.sessionExpired) {
        setStep({ kind: 'session-expired', expiredAt: result.expiredAt });
        return;
      }
      // Falha sem sessão expirada (rede, schema inesperado — CA-07): fica na
      // tela de carregamento, que já exibe `eventConfig.errorMessage` com a
      // opção de repetir — nunca quebra em tela branca.
      if (!result.success) return;

      const { activationConfig, consent } = result.config;
      const activationType = readActivationType(activationConfig);
      setStep({ kind: 'resolving-plugin', session, consent, activationConfig, activationType });

      const outcome = await pluginRegistry.resolve(session, activationType);
      applyPluginRegistryOutcome(outcome, session, consent, activationConfig);
    },
    [eventConfig, pluginRegistry, applyPluginRegistryOutcome]
  );

  // LOCAL-003 (ADR-003, Adendo 2026-07-30 — Correção 4): restaura a sessão
  // de dispositivo pós-refresh a partir do espelho `activeSession` em
  // IndexedDB, sem exigir novo PIN. `[]` deliberado — roda uma única vez na
  // montagem (não a cada render nem a cada troca de passo). A guarda
  // `cancelled` é local ao closure desta invocação do efeito (não um `ref`
  // compartilhado entre invocações, ao contrário de `usePluginRegistryResolution`/
  // `usePluginLoadRetry`): sob `React.StrictMode`, a 1ª invocação (setup ->
  // cleanup síncronos) fecha `cancelled=true` antes do `await
  // activeSessionRepository.get()` resolver, então só a 2ª invocação chega a
  // chamar `setStep`/`runLoadConfig` — exatamente 1 sequência de requisições
  // (regressão HUB-119).
  //
  // Sem snapshot ou com `isActiveSessionExpired` (token já vencido): mantém
  // `pin-entry` (comportamento atual, sem mudança). Com snapshot válido,
  // reexecuta o mesmo pipeline já usado após o login por PIN
  // (`loading-config` -> `runLoadConfig`) — a restauração aterrissa sempre em
  // `lead-capture` ou `plugin-consent-gate` (nunca em `plugin`, que só é
  // alcançado via `handlePluginConsentDecision`, ação explícita do
  // participante/staff), preservando o fix de vazamento de consentimento
  // entre participantes da HUB-26.
  useEffect(() => {
    let cancelled = false;

    void (async (): Promise<void> => {
      const snapshot = await activeSessionRepository.get();
      if (cancelled) return;
      if (!snapshot || isActiveSessionExpired(snapshot, new Date())) return;

      const session = toDeviceSessionFromSnapshot(snapshot);
      setStep({ kind: 'loading-config', session });
      await runLoadConfig(session);
    })();

    return (): void => {
      cancelled = true;
    };
  }, []);

  const handlePinSubmit = useCallback(
    async (pin: string): Promise<void> => {
      const session = await pinAuth.authenticate(pin, DEVICE_LABEL);
      if (!session) return;
      setStep({ kind: 'loading-config', session });
      await runLoadConfig(session);
    },
    [pinAuth, runLoadConfig]
  );

  const handleRetryConfig = useCallback((): void => {
    if (step.kind !== 'loading-config') return;
    void runLoadConfig(step.session);
  }, [step, runLoadConfig]);

  const handleRetryPluginRegistry = useCallback((): void => {
    if (step.kind !== 'resolving-plugin') return;
    const { session, consent, activationConfig, activationType } = step;
    void pluginRegistry
      .retryManually(session, activationType)
      .then((outcome) => applyPluginRegistryOutcome(outcome, session, consent, activationConfig));
  }, [step, pluginRegistry, applyPluginRegistryOutcome]);

  const handleEndSession = useCallback((): void => {
    // "Limpa sessão local" (CA-03 da HUB-235): a sub-sessão só existe em
    // memória neste componente — voltar para `pin-entry` já descarta a
    // referência. Limpa também o espelho em IndexedDB (HUB-236): evita que o
    // Service Worker tente sincronizar em segundo plano com um token que já
    // se sabe inválido (Spec Técnica > Riscos técnicos, item 1) e que o
    // próximo boot restaure uma sessão encerrada (LOCAL-003).
    void activeSessionRepository.clear();
    setStep({ kind: 'pin-entry' });
  }, []);

  const handleBackFromExpired = useCallback((): void => {
    handleEndSession();
  }, [handleEndSession]);

  const handleLeadSubmit = useCallback(
    async (input: FakeLeadInput): Promise<void> => {
      if (step.kind !== 'lead-capture') return;
      const consentTextVersion = resolveConsentVersion(step.consent);
      const result = await leadCapture.captureLead(step.session, input, consentTextVersion);

      if (!result.success) {
        // CA-09: fila cheia — abre o modal bloqueante; o formulário
        // continua disponível para nova tentativa (Design > Modal de
        // Bloqueio). 'invalid-input'/'storage-unavailable' já são tratados
        // por `leadCapture.errorMessage`/silêncio (CA-03) — permanece na
        // tela de captura sem outra ação.
        if (result.reason === 'queue-full') setQueueFullVisible(true);
        return;
      }

      // CA-04: lead enfileirado — agenda a sincronização (Background Sync
      // ou fallback de polling).
      notifyLeadEnqueued();
      if (result.showQueueAlert) setQueueAlertVisible(true);

      setStep({
        kind: 'success',
        session: step.session,
        consent: step.consent,
        leadName: result.leadName,
      });
    },
    [step, leadCapture, notifyLeadEnqueued]
  );

  const handleCaptureAnother = useCallback((): void => {
    if (step.kind !== 'success') return;
    setStep({ kind: 'lead-capture', session: step.session, consent: step.consent });
  }, [step]);

  const handlePluginConsentDecision = useCallback(
    (consentAccepted: boolean): void => {
      if (step.kind !== 'plugin-consent-gate') return;
      const { session, consent, entry, activationConfig } = step;
      setStep({
        kind: 'plugin',
        session,
        consent,
        entry,
        activationConfig,
        consentAccepted,
        consentTextVersion: resolveConsentVersion(consent),
      });
      pluginLoad.start(entry);
    },
    [step, pluginLoad]
  );

  const handlePluginConsentApprove = useCallback(
    (): void => handlePluginConsentDecision(true),
    [handlePluginConsentDecision]
  );
  const handlePluginConsentDecline = useCallback(
    (): void => handlePluginConsentDecision(false),
    [handlePluginConsentDecision]
  );

  const handlePluginLeadCapture = useCallback(
    (lead: LeadData): void => {
      if (step.kind !== 'plugin') return;
      const { session, consentAccepted, consentTextVersion } = step;
      void pluginLeadCapture
        .captureLead(session, lead, consentAccepted, consentTextVersion)
        .then((result) => {
          if (!result.success) {
            // CA-09 (HUB-236): fila cheia — mesmo modal bloqueante do
            // caminho fake. 'storage-unavailable' já é logado internamente
            // pelo use case (CA-03), sem exposição em UI.
            if (result.reason === 'queue-full') setQueueFullVisible(true);
            return;
          }
          notifyLeadEnqueued();
          if (result.showQueueAlert) setQueueAlertVisible(true);
        });
    },
    [step, pluginLeadCapture, notifyLeadEnqueued]
  );

  const handlePluginComplete = useCallback((): void => {
    if (step.kind !== 'plugin') return;
    // ADR-004 (`PluginProps.onComplete`) não define o comportamento do
    // Runtime após a mecânica do plugin concluir — nenhum CA desta fatia
    // cobre este ponto. Decisão mínima e documentada, não uma arquitetura
    // nova: volta ao consent gate para o próximo participante, mesmo padrão
    // de `handleCaptureAnother` no caminho fake (Design > Estado "Sucesso":
    // o próprio plugin é a confirmação visual, sem tela extra aqui).
    setStep({
      kind: 'plugin-consent-gate',
      session: step.session,
      consent: step.consent,
      entry: step.entry,
      activationConfig: step.activationConfig,
    });
  }, [step]);

  const dismissQueueAlert = useCallback((): void => setQueueAlertVisible(false), []);
  const closeQueueFullModal = useCallback((): void => setQueueFullVisible(false), []);

  // CA-13/CA-14/CA-21: telemetria best-effort do carregamento do plugin —
  // registrada quando `pluginLoad.state` alcança um desfecho terminal
  // (sucesso ou erro final), independente de ter vindo da tentativa
  // automática inicial ou de uma retentativa (manual ou via
  // `PluginErrorBoundary`). Guardado por identidade de objeto para nunca
  // duplicar o registro entre re-renders — inclusive sob `React.StrictMode`
  // (mesma disciplina da HUB-119).
  useEffect(() => {
    const loadState = pluginLoad.state;
    if (loadState === reportedLoadStateRef.current) return;
    if (loadState.kind !== 'ready' && loadState.kind !== 'error-final') return;
    reportedLoadStateRef.current = loadState;
    if (step.kind !== 'plugin') return;

    const activationType = step.entry.activationType;
    const event: PluginLoadEvent =
      loadState.kind === 'ready'
        ? {
            activationType,
            success: true,
            loadDurationMs: loadState.loadDurationMs,
            retryCount: loadState.retryCount,
          }
        : {
            activationType,
            success: false,
            loadDurationMs: loadState.loadDurationMs,
            retryCount: loadState.retryCount,
            lastErrorCode: loadState.errorCode,
            lastErrorMessage: loadState.errorMessage,
          };
    void recordPluginLoadEventUseCase.execute(step.session, event);
  }, [pluginLoad.state, step]);

  return {
    step,
    pinAuth,
    eventConfig,
    leadCapture,
    pluginRegistry,
    pluginLoad,
    indicatorState,
    pendingCount,
    queueAlertVisible,
    queueFullVisible,
    dismissQueueAlert,
    closeQueueFullModal,
    handlePinSubmit,
    handleRetryConfig,
    handleBackFromExpired,
    handleEndSession,
    handleLeadSubmit,
    handleCaptureAnother,
    handleRetryPluginRegistry,
    handlePluginConsentApprove,
    handlePluginConsentDecline,
    handlePluginLeadCapture,
    handlePluginComplete,
  };
}
